import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material'
import { useResetPasswordUserMutation } from 'src/services/user-service.js'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import { Logo } from 'src/components/logo'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { setTheme, setSEODetails, useCheckDomainQuery } from 'src/services/theme-service'
import { useDispatch } from 'react-redux'

export function WhiteLabelResetPassword () {
  let currentDomain

  if (typeof window !== 'undefined') {
    currentDomain = window.location.hostname
  }

  const [whiteLabelTheme, setWhiteLabelTheme] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [resetPasswordUser, { isLoading: isResetingPassword }] = useResetPasswordUserMutation()
  const { data: whiteLabelUser } = useCheckDomainQuery({ domain: currentDomain })

  useEffect(() => {
    setWhiteLabelTheme(whiteLabelUser?.data?.theme)
    if (whiteLabelUser?.data?.seo) {
      dispatch(setSEODetails({ ...whiteLabelUser?.data?.seo }))
      dispatch(setTheme({ ...whiteLabelUser?.data?.theme }))
    }
  }, [
    whiteLabelUser?.data?.theme,
    whiteLabelUser?.data?.seo
  ])

  const formikUpdatePassword = useFormik({
    initialValues: {
      newPassword: '',
      confirmNewPassword: ''
    },
    validationSchema: Yup.object().shape({
      newPassword: Yup.string().min(8, 'Password must be at least 8 characters')
        .max(255)
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[#@$!%*?&]/, 'Password must contain at least one special character')
        .required('New password is required'),
      confirmNewPassword: Yup.string()
        .required('Confirm new password is required')
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    }),
    onSubmit: async (values, helpers) => {
      try {
        const { message } = await resetPasswordUser({ ...values, id: token }).unwrap()
        toast.success(message)
        helpers.resetForm()
        if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
          window.localStorage.clear()
        }
        navigate('/w-login')
      } catch (err) {
        toast.error(err.data.error.message)
      }
    }
  })

  const fields = [
    {
      name: 'newPassword',
      placeholder: 'new password',
      show: showPassword,
      toggle: () => setShowPassword((prev) => !prev)
    },
    {
      name: 'confirmNewPassword',
      placeholder: 'confirm new password',
      show: showConfirmPassword,
      toggle: () => setShowConfirmPassword((prev) => !prev)
    }
  ]

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F2F4F6'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: isMdUp ? '500px' : '100%',
          height: '100%',
          p: 4,
          backgroundColor: '#FFFFFF'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '160px',
            height: '30px',
            mb: 5
          }}
        >
          {/* <Logo /> */}
          {whiteLabelTheme?.logo ? <img src={whiteLabelTheme?.logo} style={{ height: '36px', width: 'auto' }} /> : <Logo />}
        </Box>

        <Box
          sx={{
            width: '100%',
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '12px',
            height: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <Typography
              sx={{
                width: '100%',
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: '30px',
                color: `${theme.palette.primary.primaryText}`
              }}
            >
              Reset your password
            </Typography>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '5px'
            }}
          >
            {fields.map(({ name, placeholder, show, toggle }) => (
              <Stack key={name} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  type={show ? 'text' : 'password'}
                  placeholder={placeholder}
                  id={name}
                  name={name}
                  variant='outlined'
                  onChange={formikUpdatePassword.handleChange}
                  onBlur={formikUpdatePassword.handleBlur}
                  value={formikUpdatePassword.values[name]}
                  error={
                    formikUpdatePassword.touched[name] &&
                    Boolean(formikUpdatePassword.errors[name])
                  }
                  helperText={
                    formikUpdatePassword.touched[name] &&
                    formikUpdatePassword.errors[name]
                  }
                  sx={{
                    mt: 2,
                    width: '100%',
                    height: 40,
                    backgroundColor: 'white',
                    '& div': { pl: 0.3 },
                    '& div fieldset': { borderRadius: '8px', border: '1px solid #E4E4E5' },
                    '& div input': {
                      py: 1.3,
                      fontSize: '13px',
                      fontWeight: 400,
                      lineHeight: '16px',
                      letterSpacing: '0em',
                      '&::placeholder': {
                        color: 'rgba(40, 40, 123, 0.5)'
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={toggle}
                          edge='end'
                          aria-label='toggle password visibility'
                        >
                          {show ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Stack>
            ))}

            <Button
              onClick={formikUpdatePassword.handleSubmit}
              variant='contained'
              fullWidth
              sx={{ p: 1, mt: 3 }}
            >
              {isResetingPassword ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Reset Password'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
